/**
 * Terms of Service Page
 *
 * Terms of service for Have-Done.
 */

import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Footer } from './Footer';

export function TermsPage() {
  return (
    <div className="min-h-screen bg-bg-primary flex flex-col">
      <div className="mx-auto max-w-3xl w-full px-3 sm:px-4 py-8 flex-1">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to app
        </Link>

        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <FileText className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold text-text-primary">
              Terms of Service
            </h1>
            <p className="text-sm text-text-secondary">
              Last updated: January 2025
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-slate max-w-none space-y-8">
          {/* Introduction */}
          <section>
            <p className="text-text-secondary leading-relaxed">
              Welcome to Have-Done! These Terms of Service ("Terms") govern your use of
              the Have-Done application and services. By using Have-Done, you agree to
              these Terms.
            </p>
          </section>

          {/* What is Have-Done */}
          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              1. What is Have-Done?
            </h2>
            <p className="text-text-secondary">
              Have-Done is a personal productivity application that helps you track and
              celebrate your accomplishments. Unlike traditional to-do apps, Have-Done
              focuses on what you've achieved rather than what remains to be done.
            </p>
          </section>

          {/* Your Account */}
          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              2. Your Account
            </h2>
            <ul className="text-text-secondary space-y-2">
              <li>You must be at least 16 years old to use Have-Done</li>
              <li>You're responsible for maintaining the security of your account</li>
              <li>You're responsible for all activities under your account</li>
              <li>One person per account - no sharing accounts</li>
            </ul>
          </section>

          {/* Acceptable Use */}
          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              3. Acceptable Use
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="bg-success/5 border border-success/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-success" />
                  <h3 className="font-medium text-text-primary">You May</h3>
                </div>
                <ul className="text-sm text-text-secondary space-y-1">
                  <li>Log personal and professional achievements</li>
                  <li>Use Have-Done for personal productivity</li>
                  <li>Export your data at any time</li>
                  <li>Delete your account at any time</li>
                </ul>
              </div>
              <div className="bg-error/5 border border-error/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <XCircle className="w-5 h-5 text-error" />
                  <h3 className="font-medium text-text-primary">You May Not</h3>
                </div>
                <ul className="text-sm text-text-secondary space-y-1">
                  <li>Use the service for illegal purposes</li>
                  <li>Attempt to access other users' data</li>
                  <li>Reverse engineer or hack the service</li>
                  <li>Use automated tools to access the service</li>
                  <li>Store harmful or offensive content</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Your Content */}
          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              4. Your Content
            </h2>
            <p className="text-text-secondary mb-4">
              You own all content you create in Have-Done (your entries, notes, etc.).
            </p>
            <ul className="text-text-secondary space-y-2">
              <li>
                <strong className="text-text-primary">Ownership:</strong> Your content belongs to you
              </li>
              <li>
                <strong className="text-text-primary">License to us:</strong> You grant us a limited license
                to store and display your content to you
              </li>
              <li>
                <strong className="text-text-primary">Backup:</strong> While we maintain backups, you should
                also export your data regularly
              </li>
            </ul>
          </section>

          {/* Service Availability */}
          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              5. Service Availability
            </h2>
            <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-accent-dark shrink-0 mt-0.5" />
                <div>
                  <p className="text-text-secondary text-sm">
                    Have-Done is provided "as is". While we strive for high availability,
                    we cannot guarantee uninterrupted access. We may also modify or
                    discontinue features with reasonable notice.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Premium Features */}
          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              6. Premium Features (Future)
            </h2>
            <p className="text-text-secondary">
              We may offer premium features in the future. If so:
            </p>
            <ul className="text-text-secondary space-y-2 mt-2">
              <li>Pricing will be clearly displayed before purchase</li>
              <li>Subscriptions can be cancelled at any time</li>
              <li>Refunds are handled according to applicable laws</li>
              <li>Free features will remain free</li>
            </ul>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              7. Termination
            </h2>
            <p className="text-text-secondary mb-4">
              You can delete your account at any time. We may also terminate accounts that:
            </p>
            <ul className="text-text-secondary space-y-2">
              <li>Violate these Terms</li>
              <li>Are used for illegal activities</li>
              <li>Are inactive for more than 2 years</li>
            </ul>
            <p className="text-text-secondary mt-4">
              We'll provide 30 days notice before termination (except for serious violations)
              and allow you to export your data.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              8. Limitation of Liability
            </h2>
            <p className="text-text-secondary">
              To the maximum extent permitted by law, Have-Done and its creators are not
              liable for any indirect, incidental, or consequential damages arising from
              your use of the service. Our total liability is limited to the amount you
              paid us in the past 12 months (or €100, whichever is greater).
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              9. Changes to Terms
            </h2>
            <p className="text-text-secondary">
              We may update these Terms occasionally. We'll notify you of significant changes
              via email or in-app notification at least 30 days before they take effect.
              Continued use after changes means you accept the new Terms.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              10. Governing Law
            </h2>
            <p className="text-text-secondary">
              These Terms are governed by the laws of the Netherlands. Any disputes will
              be resolved in the courts of Amsterdam, Netherlands.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              11. Contact
            </h2>
            <p className="text-text-secondary">
              Questions about these Terms? Contact us at{' '}
              <a href="mailto:legal@have-done.app" className="text-primary hover:underline">
                legal@have-done.app
              </a>
            </p>
          </section>

          {/* Summary */}
          <section className="border-t border-border-light pt-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              TL;DR (Plain English Summary)
            </h2>
            <div className="bg-bg-secondary rounded-lg p-4">
              <ul className="text-sm text-text-secondary space-y-2">
                <li>Your data is yours - we just help you store and view it</li>
                <li>Don't do anything illegal or harmful</li>
                <li>We'll try our best to keep the service running smoothly</li>
                <li>You can leave and take your data with you anytime</li>
                <li>We'll tell you before making big changes</li>
              </ul>
            </div>
          </section>
        </div>

      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
